import { NICHES, NICHE_PATH_LIST } from "./niches";

/** In-app routes (React Router). API paths live in configs/routes.js */

export const APP_ROUTES = {
    LANDING: "/",
    HOME: "/",
    CAMPAIGNS: "/campaigns",
    POSTS: "/posts",
    CONTENT_AI: "/content-ai",
    CAMPAIGN_WIZARD: "/campaign-wizard",
    PROFILE: "/profile",
    PROFILE_CREATE: "/profile/create",
    SETTINGS: "/settings",
    PLANS: "/plans",
    PAYMENTS: "/payments",
    PAYMENT_SUCCESS: "/payment-success",
    PAYMENT_FAILED: "/payment-failed",
    LOGIN: "/login",
    REGISTER: "/register",
    SIGNUP: "/signup",
    ACTIVATE: "/activate",
    FORGOT_PASSWORD: "/forgot-password",
    RESET_PASSWORD: "/reset-password",
    ACCOUNT_LOCKED: "/account-locked",
    OPPORTUNITIES: "/opportunities",
    NOT_FOUND: "/404",
    NICHE_HEALTHCARE: NICHES.HEALTHCARE.path,
    NICHE_BEAUTY_WELLNESS: NICHES.BEAUTY_WELLNESS.path,
    NICHE_FITNESS_WELLNESS: NICHES.FITNESS_WELLNESS.path,
    NICHE_HOSPITALITY_FOOD: NICHES.HOSPITALITY_FOOD.path,
    NICHE_PROFESSIONAL_HOME_SERVICES: NICHES.PROFESSIONAL_HOME_SERVICES.path,
    NICHE_CREATIVE_LOCAL: NICHES.CREATIVE_LOCAL.path,
};

/** Relative segment under HOME for nested router children (e.g. `/posts` → `posts`). */
export function toAppChildPath(route) {
    if (!route || route === APP_ROUTES.HOME || route === "/") return "";

    const normalized = normalizeAppPath(route);
    if (!normalized || normalized === "/") return "";

    return normalized.startsWith("/") ? normalized.slice(1) : normalized;
}

/** Strip leading slash for top-level router paths (e.g. `/login` → `login`). */
export function toRouterPath(route) {
    return route.replace(/^\//, "");
}

/** Allowed `ref` query values when entering the campaign wizard */
export const WIZARD_REF_ROUTES = [
    APP_ROUTES.HOME,
    APP_ROUTES.CONTENT_AI,
    ...NICHE_PATH_LIST,
];

export function normalizeAppPath(path) {
    if (!path || typeof path !== "string") return null;
    const trimmed = path.trim();
    if (!trimmed) return null;
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function isValidWizardRef(ref) {
    const path = normalizeAppPath(ref);
    return path != null && WIZARD_REF_ROUTES.includes(path);
}

export function buildCampaignPath(campaignId = null) {
    if (!campaignId) return APP_ROUTES.CAMPAIGNS;
    return `${APP_ROUTES.CAMPAIGNS}/${encodeURIComponent(campaignId)}`;
}

/** Parse truthy query flags like `true`, `1`, `yes`. */
export function isTruthyQueryParam(value) {
    if (value == null) return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
}

/**
 * @param {string|null} ref - Back-navigation route (`?ref=`)
 * @param {string|null} [campaignId]
 * @param {{ fastForward?: boolean, assetType?: string|null }} [options] - `fast_forward=true` skips to the generate-posts step
 */
export function buildCampaignWizardPath(ref, campaignId = null, options = {}) {
    const { fastForward = false, assetType = null } = options;
    const base = campaignId
        ? `${APP_ROUTES.CAMPAIGN_WIZARD}/${encodeURIComponent(campaignId)}`
        : APP_ROUTES.CAMPAIGN_WIZARD;

    const params = new URLSearchParams();
    if (isValidWizardRef(ref)) {
        params.set("ref", normalizeAppPath(ref));
    }
    if (fastForward) {
        params.set("fast_forward", "true");
    }
    if (assetType) {
        params.set("asset_type", String(assetType));
    }

    const query = params.toString();
    return query ? `${base}?${query}` : base;
}
